import { useState, useCallback } from "react";
import { toast } from "sonner";
import Layout from "../components/Layout";
import api from "../lib/api";
import { MagnifyingGlass, Barcode, WarningCircle } from "@phosphor-icons/react";

const th = {
  textAlign: "left", padding: "9px 12px", fontSize: 11, fontWeight: 800, color: "#64748B",
  textTransform: "uppercase", letterSpacing: 0.4, borderBottom: "2px solid #E2E8F0", whiteSpace: "nowrap",
};
const td = { padding: "9px 12px", fontSize: 13, borderBottom: "1px solid #F1F5F9", verticalAlign: "top" };

export default function RmpLookup() {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [note, setNote] = useState("");

  const search = useCallback((term) => {
    const query = (term ?? q).trim();
    if (query.length < 2) {
      toast.error("Type at least 2 characters to search.");
      return;
    }
    setLoading(true);
    setNote("");
    api.get("/admin/rmp-lookup", { params: { q: query } })
      .then((r) => {
        setRows(r.data.rows || []);
        setNote(r.data.note || "");
        setSearched(true);
      })
      .catch((e) => {
        setRows([]);
        setSearched(true);
        toast.error(e.response?.data?.detail || "Lookup failed");
      })
      .finally(() => setLoading(false));
  }, [q]);

  const hl = (text) => {
    if (!text || !q.trim()) return text || "—";
    const i = text.toLowerCase().indexOf(q.trim().toLowerCase());
    if (i < 0) return text;
    return (
      <>
        {text.slice(0, i)}
        <mark style={{ background: "#FEF08A", padding: 0 }}>{text.slice(i, i + q.trim().length)}</mark>
        {text.slice(i + q.trim().length)}
      </>
    );
  };

  return (
    <Layout>
      <div data-testid="rmp-lookup-page" style={{ maxWidth: 1000, margin: "0 auto" }}>
        <div style={{ marginBottom: 18 }}>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: "#0F172A", margin: 0, display: "flex", alignItems: "center", gap: 10 }}>
            <Barcode size={26} weight="duotone" style={{ color: "#2A5C9B" }} /> RMP Code Lookup
          </h1>
          <p style={{ fontSize: 13, color: "#64748B", margin: "6px 0 0", maxWidth: 720 }}>
            Search the ERP item master (<code>dbo.item</code>) by <b>item number</b>, <b>item name</b> or
            <b> RMP code</b> to find the mapped RMP code for any product.
          </p>
        </div>

        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          <div style={{ position: "relative", flex: 1 }}>
            <MagnifyingGlass size={16} style={{ position: "absolute", left: 12, top: 12, color: "#94A3B8" }} />
            <input
              data-testid="rmp-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") search(); }}
              placeholder="Item number, item name, or RMP code…"
              style={{ width: "100%", padding: "10px 12px 10px 36px", fontSize: 14, border: "1px solid #CBD5E1", borderRadius: 8, outline: "none", boxSizing: "border-box" }}
            />
          </div>
          <button
            data-testid="rmp-search-btn"
            onClick={() => search()}
            disabled={loading}
            style={{ padding: "10px 22px", fontSize: 14, fontWeight: 700, borderRadius: 8, border: "none", background: "#2A5C9B", color: "white", cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.6 : 1 }}>
            {loading ? "Searching…" : "Search"}
          </button>
        </div>

        <div style={{ background: "white", border: "1px solid #E2E8F0", borderRadius: 12, overflow: "hidden" }}>
          <div style={{ padding: "10px 14px", background: "#F8FAFC", borderBottom: "1px solid #E2E8F0", fontSize: 12, fontWeight: 700, color: "#475569" }}>
            {searched ? `${rows.length} result${rows.length === 1 ? "" : "s"}` : "Enter a search to begin"}
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ ...th, width: 200 }}>Item Number</th>
                  <th style={th}>Item Name</th>
                  <th style={{ ...th, width: 200 }}>RMP Code</th>
                </tr>
              </thead>
              <tbody data-testid="rmp-results-body">
                {loading && (
                  <tr><td style={{ ...td, textAlign: "center", padding: 28, color: "#94A3B8" }} colSpan={3}>Searching the ERP item master…</td></tr>
                )}
                {!loading && searched && rows.length === 0 && (
                  <tr><td style={{ ...td, textAlign: "center", padding: 28, color: "#94A3B8" }} colSpan={3} data-testid="rmp-empty">
                    <WarningCircle size={18} style={{ display: "inline", marginRight: 6, verticalAlign: "-3px" }} />
                    {note || "No items match your search."}
                  </td></tr>
                )}
                {!loading && rows.map((r, i) => (
                  <tr key={`${r.icode}-${i}`} data-testid={`rmp-row-${r.icode || i}`} style={i % 2 ? { background: "#FCFCFD" } : undefined}>
                    <td style={{ ...td, fontWeight: 700, color: "#0F172A", whiteSpace: "nowrap" }}>{hl(r.icode)}</td>
                    <td style={{ ...td, color: "#334155" }}>{hl(r.iname)}</td>
                    <td style={{ ...td, whiteSpace: "nowrap" }}>
                      {r.rmpcode
                        ? <span style={{ display: "inline-block", padding: "2px 10px", borderRadius: 999, background: "#EEF2FF", color: "#3730A3", fontWeight: 800, fontSize: 12.5 }}>{hl(r.rmpcode)}</span>
                        : <span style={{ color: "#CBD5E1" }}>— no RMP code —</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
}
